import { gzipSync } from 'fflate';
import { CONTENT_BUCKET, IMPORT_BATCH_SIZE } from '../config';
import { chunk } from './chunks';
import { compilePuzzle } from './contentCompiler';
import { updateManifest, serializeManifest } from './contentManifest';
import { fetchAllRows } from './data';
import { exactArrayBuffer, sha256Hex } from './hash';
import { writePackSqlite } from './sqliteContentWriter';
import { approvedIdsToPublish } from './publicationState';
import { supabase } from './supabase';
import type { AnswerAlias, Category, ContentManifestPack, ContentManifestV2, ContentPack, ContentRelease, EquivalenceRule, Riddle } from './types';

export interface ReleaseBuildInput { name: string; notes: string; languageCode: string; contentPackId: string; userId: string; onProgress?: (message: string, progress: number) => void; }
export interface ReleaseBuildResult { release: ContentRelease; manifestUrl: string; packUrl: string; packBytes: number; uncompressedBytes: number; puzzleCount: number; riddleCount: number; sha256: string; schemaVersion: 2; packVersion: number; manifest: ContentManifestV2; }

function report(callback: ReleaseBuildInput['onProgress'], message: string, progress: number) { callback?.(message, progress); }

export function selectReferencedCategories(categories: Category[], riddles: Riddle[]): Category[] {
  const usedCategories = new Set(riddles.map((riddle) => riddle.category_id));
  const selectedCategories = categories.filter((category) => usedCategories.has(category.id));
  const availableCategories = new Set(selectedCategories.map((category) => category.id));
  const missingCategory = [...usedCategories].find((id) => !availableCategories.has(id));
  if (missingCategory) throw new Error(`Catégorie référencée absente : ${missingCategory}.`);
  return selectedCategories;
}

async function loadRiddles(languageCode: string, packId: string): Promise<Riddle[]> {
  const rows: Riddle[] = []; let from = 0;
  while (true) {
    const { data, error } = await supabase.from('riddles').select('*').eq('language_code', languageCode).eq('content_pack_id', packId).in('status', ['approved', 'published']).order('id').range(from, from + 999);
    if (error) throw error; const batch = (data ?? []) as Riddle[]; rows.push(...batch);
    if (batch.length < 1000) return rows; from += 1000;
  }
}

async function loadAliases(ids: string[]): Promise<AnswerAlias[]> {
  const rows: AnswerAlias[] = [];
  for (const batch of chunk(ids, 200)) { const { data, error } = await supabase.from('answer_aliases').select('*').in('riddle_id', batch).order('normalized_display'); if (error) throw error; rows.push(...((data ?? []) as AnswerAlias[])); }
  return rows;
}

async function readActiveManifest(): Promise<unknown | null> {
  const { data, error } = await supabase.storage.from(CONTENT_BUCKET).download('manifest.json');
  if (error) {
    const message = error.message.toLowerCase();
    if (message.includes('not found') || message.includes('object not found')) return null;
    throw error;
  }
  return JSON.parse(await data.text()) as unknown;
}

export async function loadActiveManifest(): Promise<ContentManifestV2 | null> {
  const value = await readActiveManifest();
  if (value === null) return null;
  const { parseManifestV2 } = await import('./contentPackSchema');
  return parseManifestV2(value);
}

export async function buildAndPublishRelease(input: ReleaseBuildInput): Promise<ReleaseBuildResult> {
  let releaseId: number | null = null;
  let approvedIds: string[] = [];
  let approvedTransitioned = false;
  try {
    report(input.onProgress, 'Création de la publication…', 4);
    const { data: releaseData, error: releaseError } = await supabase.from('content_releases').insert({ name: input.name, notes: input.notes || null, status: 'building', schema_version: 2, created_by: input.userId }).select('*').single();
    if (releaseError) throw releaseError; releaseId = Number(releaseData.id);

    report(input.onProgress, 'Chargement du pack et des référentiels…', 10);
    const [packs, categories, rules, currentManifest] = await Promise.all([
      fetchAllRows<ContentPack>('content_packs'), fetchAllRows<Category>('categories'), fetchAllRows<EquivalenceRule>('equivalence_rules'), readActiveManifest(),
    ]);
    const pack = packs.find((item) => item.id === input.contentPackId);
    if (!pack || pack.language_code !== input.languageCode) throw new Error(`Pack introuvable ou langue incohérente : ${input.contentPackId}.`);
    if (pack.status !== 'active') throw new Error(`Le pack ${pack.id} n’est pas actif.`);

    report(input.onProgress, 'Chargement des énigmes publiables…', 18);
    const riddles = await loadRiddles(input.languageCode, pack.id);
    if (riddles.length === 0) throw new Error('Aucune énigme approved ou published pour ce pack.');
    const aliases = await loadAliases(riddles.map((riddle) => riddle.id));
    const aliasesByRiddle = new Map<string, AnswerAlias[]>();
    for (const alias of aliases) { const list = aliasesByRiddle.get(alias.riddle_id) ?? []; list.push(alias); aliasesByRiddle.set(alias.riddle_id, list); }
    const globalRules = rules.filter((rule) => rule.content_pack_id === pack.id && rule.language_code === pack.language_code && rule.puzzle_id === null && rule.is_active);
    const selectedCategories = selectReferencedCategories(categories, riddles);

    const generatedAt = new Date().toISOString();
    report(input.onProgress, `Compilation de ${riddles.length} énigmes…`, 32);
    const compiledPuzzles = riddles.map((riddle) => compilePuzzle(
      riddle,
      aliasesByRiddle.get(riddle.id) ?? [],
      [...globalRules, ...rules.filter((rule) => rule.puzzle_id === riddle.id && rule.is_active)],
    ));
    const sqliteBytes = await writePackSqlite({ pack, version: releaseId, generatedAt, categories: selectedCategories, puzzles: compiledPuzzles, globalRules }, releaseId);
    report(input.onProgress, 'Compression et calcul du SHA-256…', 55);
    const compressed = gzipSync(sqliteBytes, { level: 9 });
    const sha256 = await sha256Hex(compressed);
    const safePackName = pack.id.replace(/[^a-z0-9_-]/gi, '_');
    const packPath = `releases/${releaseId}/${safePackName}.sqlite.gz`;
    const { data: packPublic } = supabase.storage.from(CONTENT_BUCKET).getPublicUrl(packPath);
    const manifestPack: ContentManifestPack = { id: pack.id, languageCode: pack.language_code, version: releaseId, status: 'active', url: packPublic.publicUrl, sha256, sizeBytes: compressed.byteLength, uncompressedSizeBytes: sqliteBytes.byteLength, puzzleCount: riddles.length };
    const manifest = updateManifest(currentManifest, releaseId, generatedAt, manifestPack, packs);
    const manifestBytes = serializeManifest(manifest);
    const historicalPath = `releases/${releaseId}/manifest.json`;

    report(input.onProgress, 'Téléversement du pack validé…', 66);
    const { error: packUploadError } = await supabase.storage.from(CONTENT_BUCKET).upload(packPath, new Blob([exactArrayBuffer(compressed)], { type: 'application/gzip' }), { cacheControl: '31536000', contentType: 'application/gzip', upsert: false });
    if (packUploadError) throw packUploadError;
    report(input.onProgress, 'Téléversement du manifeste historique…', 74);
    const { error: historyUploadError } = await supabase.storage.from(CONTENT_BUCKET).upload(historicalPath, new Blob([exactArrayBuffer(manifestBytes)], { type: 'application/json' }), { cacheControl: '31536000', contentType: 'application/json', upsert: false });
    if (historyUploadError) throw historyUploadError;

    const items = riddles.map((riddle) => ({ release_id: releaseId, riddle_id: riddle.id, riddle_revision: riddle.revision, operation: riddle.status === 'published' ? 'update' : 'add', content_hash: riddle.content_hash }));
    for (const batch of chunk(items, IMPORT_BATCH_SIZE)) { const { error } = await supabase.from('content_release_items').insert(batch); if (error) throw error; }
    const { error: readyError } = await supabase.from('content_releases').update({ status: 'ready', manifest_path: historicalPath }).eq('id', releaseId); if (readyError) throw readyError;

    approvedIds = approvedIdsToPublish(riddles);
    for (const batch of chunk(approvedIds, IMPORT_BATCH_SIZE)) { const { error } = await supabase.from('riddles').update({ status: 'published', published_at: generatedAt, updated_by: input.userId }).in('id', batch); if (error) throw error; }
    approvedTransitioned = approvedIds.length > 0;
    const { data: published, error: publishError } = await supabase.from('content_releases').update({ status: 'published', published_at: generatedAt }).eq('id', releaseId).select('*').single();
    if (publishError) throw publishError;
    report(input.onProgress, 'Activation du manifeste v2…', 90);
    const { error: activateError } = await supabase.storage.from(CONTENT_BUCKET).upload('manifest.json', new Blob([exactArrayBuffer(manifestBytes)], { type: 'application/json' }), { cacheControl: '60', contentType: 'application/json', upsert: true });
    if (activateError) throw activateError;
    const manifestUrl = supabase.storage.from(CONTENT_BUCKET).getPublicUrl('manifest.json').data.publicUrl;
    report(input.onProgress, 'Publication terminée.', 100);
    return { release: published as ContentRelease, manifestUrl, packUrl: packPublic.publicUrl, packBytes: compressed.byteLength, uncompressedBytes: sqliteBytes.byteLength, puzzleCount: riddles.length, riddleCount: riddles.length, sha256, schemaVersion: 2, packVersion: releaseId, manifest };
  } catch (error) {
    if (approvedTransitioned) for (const batch of chunk(approvedIds, IMPORT_BATCH_SIZE)) await supabase.from('riddles').update({ status: 'approved', published_at: null, updated_by: input.userId }).in('id', batch);
    if (releaseId !== null) await supabase.from('content_releases').update({ status: 'failed' }).eq('id', releaseId);
    throw error;
  }
}
